import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CdkDrag, CdkDragEnd, CdkDragHandle } from '@angular/cdk/drag-drop';

import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { SupportMetricsDto } from 'api';

import { AuthService } from '@auth/services/auth.service';
import { PermissionsService } from '@auth/services/permissions.service';
import { PERMISSIONS_SOPORTE } from '@auth/constants';
import { TicketFormComponent } from '@support/components/ticket-form/ticket-form.component';
import { SupportTicketsService, TicketActivityItem } from '@support/services/support-tickets.service';

const STORAGE_KEY = 'support-widget-pos';
const STORAGE_SINCE_KEY = 'support-widget-activity-since';
/** Intervalo normal del polling de actividad/métricas. */
const POLL_INTERVAL_MS = 60_000;
/** Tope del intervalo cuando el backoff crece por fallos consecutivos (5 min). */
const MAX_POLL_INTERVAL_MS = 300_000;
/** Fallos consecutivos tras los cuales se abre el circuito (outage sostenido confirmado). */
const CIRCUIT_OPEN_THRESHOLD = 4;
/** Intervalo largo mientras el circuito está abierto: solo se prueba ocasionalmente (15 min). */
const CIRCUIT_COOLDOWN_MS = 900_000;

/**
 * Extrae la espera indicada por el backend en un `429 Too Many Requests` (rate limiter, item /4):
 * el header `Retry-After` (segundos, expuesto por CORS) o, como respaldo, `data.retryAfterSeconds`
 * del body. Devuelve el valor en ms, o `0` si no es un 429 o no trae una espera válida.
 */
function parseRetryAfterMs(err: unknown): number {
  const e = err as {
    status?: number;
    headers?: { get?: (name: string) => string | null };
    error?: { data?: { retryAfterSeconds?: number } };
  } | null;
  if (!e || e.status !== 429) return 0;
  const header = e.headers?.get?.('Retry-After');
  const fromHeader = header != null ? Number(header) : NaN;
  const fromBody = e.error?.data?.retryAfterSeconds;
  const seconds = Number.isFinite(fromHeader) ? fromHeader : typeof fromBody === 'number' ? fromBody : NaN;
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
}

@Component({
  selector: 'shared-support-floating-widget',
  imports: [
    ButtonModule,
    CdkDrag,
    CdkDragHandle,
    DatePipe,
    DecimalPipe,
    SkeletonModule,
    TooltipModule,
    TicketFormComponent,
  ],
  template: `
    @if (canView()) {
      <div
        cdkDrag
        [cdkDragFreeDragPosition]="dragPos()"
        (cdkDragEnded)="onDragEnd($event)"
        class="fixed bottom-2 right-2 sm:bottom-6 sm:right-6 z-50 select-none"
      >
        <!-- Panel -->
        @if (isPanelOpen()) {
          <div
            class="absolute bottom-full right-0 mb-3 w-72 rounded-xl shadow-2xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-900 overflow-hidden"
            style="animation: fadeSlideUp 0.15s ease-out"
          >
            <!-- Cabecera -->
            <div class="flex items-center justify-between px-4 py-3 bg-primary text-white">
              <div class="flex items-center gap-2">
                <i class="pi pi-headphones text-sm"></i>
                <span class="font-semibold text-sm">Centro de soporte</span>
              </div>
              <button
                class="text-xs text-white/80 hover:text-white bg-transparent border-0 p-0 cursor-pointer hover:underline"
                (click)="goToTickets()"
              >
                Ver todos →
              </button>
            </div>

            <!-- Métricas -->
            @if (isLoadingMetrics()) {
              <div class="p-4 flex flex-col gap-2">
                <p-skeleton height="44px" borderRadius="8px" />
                <p-skeleton height="44px" borderRadius="8px" />
              </div>
            } @else if (metrics(); as m) {
              <div class="grid grid-cols-2 gap-2 p-3">
                <div class="flex flex-col gap-0.5 p-2.5 rounded-lg bg-blue-50 dark:bg-blue-950/40">
                  <span class="text-xs text-blue-600 dark:text-blue-400 font-medium">Abiertos</span>
                  <span class="text-2xl font-bold text-blue-700 dark:text-blue-300">{{ m.totalOpen ?? 0 }}</span>
                </div>
                <div class="flex flex-col gap-0.5 p-2.5 rounded-lg bg-orange-50 dark:bg-orange-950/40">
                  <span class="text-xs text-orange-600 dark:text-orange-400 font-medium">En progreso</span>
                  <span class="text-2xl font-bold text-orange-700 dark:text-orange-300">{{
                    m.totalInProgress ?? 0
                  }}</span>
                </div>
                <div
                  class="flex flex-col gap-0.5 p-2.5 rounded-lg"
                  [class]="
                    (m.totalSlaBreached ?? 0) > 0 ? 'bg-red-50 dark:bg-red-950/40' : 'bg-surface-50 dark:bg-surface-800'
                  "
                >
                  <span
                    class="text-xs font-medium"
                    [class]="(m.totalSlaBreached ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-surface-500'"
                    >SLA vencidos</span
                  >
                  <div class="flex items-center gap-1.5">
                    <span
                      class="text-2xl font-bold"
                      [class]="
                        (m.totalSlaBreached ?? 0) > 0
                          ? 'text-red-700 dark:text-red-300'
                          : 'text-surface-600 dark:text-surface-300'
                      "
                      >{{ m.totalSlaBreached ?? 0 }}</span
                    >
                    @if ((m.totalSlaBreached ?? 0) > 0) {
                      <i class="pi pi-exclamation-triangle text-red-500 text-sm"></i>
                    }
                  </div>
                </div>
                <div class="flex flex-col gap-0.5 p-2.5 rounded-lg bg-surface-50 dark:bg-surface-800">
                  <span class="text-xs text-surface-500 font-medium">Cumpl. SLA</span>
                  <span
                    class="text-2xl font-bold"
                    [class]="
                      (m.slaComplianceRate ?? 0) >= 90
                        ? 'text-green-600'
                        : (m.slaComplianceRate ?? 0) >= 70
                          ? 'text-orange-600'
                          : 'text-red-600'
                    "
                    >{{ m.slaComplianceRate ?? 0 | number: '1.0-0' }}%</span
                  >
                </div>
              </div>

              <!-- Actividad reciente -->
              @if (activityItems().length > 0) {
                <div class="px-3 pb-2">
                  <h5 class="text-xs font-semibold text-surface-500 uppercase tracking-wide mb-1.5">
                    Actividad reciente
                  </h5>
                  <ul class="flex flex-col gap-1">
                    @for (item of activityItems(); track item.id) {
                      <li
                        class="flex items-start gap-2 text-xs py-1 border-b border-surface-100 dark:border-surface-800 last:border-0"
                      >
                        <i
                          class="mt-0.5 flex-shrink-0"
                          [class]="
                            item.activityType === 'comment'
                              ? 'pi pi-comment text-blue-400'
                              : 'pi pi-refresh text-orange-400'
                          "
                        ></i>
                        <div class="min-w-0">
                          <span class="font-medium text-surface-700 dark:text-surface-300">{{
                            item.ticketNumber
                          }}</span>
                          <p class="text-surface-500 truncate">{{ item.activitySummary }}</p>
                          <p class="text-surface-400">{{ item.lastActivityAt | date: 'dd/MM HH:mm' }}</p>
                        </div>
                      </li>
                    }
                  </ul>
                </div>
              }

              <!-- Botones -->
              <div class="px-3 pb-3 flex flex-col gap-2">
                @if (canCreate()) {
                  <p-button
                    label="Crear ticket"
                    icon="pi pi-plus"
                    size="small"
                    styleClass="w-full"
                    (onClick)="openCreate()"
                  />
                }
                <p-button
                  label="Ver tickets"
                  icon="pi pi-list"
                  severity="secondary"
                  size="small"
                  styleClass="w-full"
                  (onClick)="goToTickets()"
                />
              </div>
            } @else {
              <div class="p-4 text-center text-sm text-surface-500">Sin datos disponibles</div>
              @if (canCreate()) {
                <div class="px-3 pb-3">
                  <p-button
                    label="Crear ticket"
                    icon="pi pi-plus"
                    size="small"
                    styleClass="w-full"
                    (onClick)="openCreate()"
                  />
                </div>
              }
            }

            <!-- Grip de arrastre -->
            <div
              cdkDragHandle
              class="flex items-center justify-center gap-1 py-2 border-t border-surface-100 dark:border-surface-800 cursor-grab active:cursor-grabbing hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
            >
              <i class="pi pi-ellipsis-h text-xs text-surface-400"></i>
              <span class="text-xs text-surface-400 select-none">Arrastrar</span>
              <i class="pi pi-ellipsis-h text-xs text-surface-400"></i>
            </div>
          </div>
        }

        <!-- FAB + asa de arrastre -->
        <div class="flex flex-col items-center gap-1">
          <div cdkDragHandle class="drag-handle" pTooltip="Arrastrar" tooltipPosition="left">
            <i class="pi pi-ellipsis-h"></i>
            <i class="pi pi-ellipsis-h"></i>
          </div>

          <div class="relative">
            <p-button
              icon="pi pi-headphones"
              [rounded]="true"
              size="large"
              (onClick)="togglePanel()"
              styleClass="support-fab shadow-lg"
              [pTooltip]="isPanelOpen() ? '' : 'Centro de Soporte'"
              tooltipPosition="left"
              aria-label="Centro de soporte"
            />
            <!-- Badge de notificación -->
            @if (hasActivity() && !isPanelOpen()) {
              <span
                class="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center shadow-md pointer-events-none"
                aria-label="Actividad nueva en soporte"
              >
                {{ activityCount() > 9 ? '9+' : activityCount() }}
              </span>
            }
          </div>
        </div>
      </div>

      <support-ticket-form
        [visible]="showModal()"
        (visibleChange)="showModal.set($event)"
        (saved)="onTicketCreated()"
      />
    }
  `,
  styles: `
    @keyframes fadeSlideUp {
      from {
        opacity: 0;
        transform: translateY(6px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    :host ::ng-deep .support-fab.p-button {
      width: 3.75rem !important;
      height: 3.75rem !important;
    }

    :host ::ng-deep .support-fab.p-button .p-button-icon {
      font-size: 1.4rem !important;
    }

    /* Móvil: el botón se encoge y se pega más a la esquina para no tapar los
       controles de la página (toggles de vista, celdas de tabla, tarjetas KPI). */
    @media (max-width: 639.98px) {
      :host ::ng-deep .support-fab.p-button {
        width: 2.75rem !important;
        height: 2.75rem !important;
        opacity: 0.9;
      }

      :host ::ng-deep .support-fab.p-button .p-button-icon {
        font-size: 1.05rem !important;
      }
    }

    .drag-handle {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      padding: 4px 12px;
      border-radius: 6px;
      cursor: grab;
      color: var(--p-surface-400);
      transition:
        background 0.15s,
        color 0.15s;
    }

    .drag-handle:active {
      cursor: grabbing;
    }

    .drag-handle:hover {
      background: var(--p-surface-100);
      color: var(--p-surface-600);
    }

    :host-context(.app-dark) .drag-handle:hover {
      background: var(--p-surface-800);
    }

    .drag-handle i {
      font-size: 0.65rem;
      line-height: 1;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportFloatingWidgetComponent implements OnInit, OnDestroy {
  private readonly supportService = inject(SupportTicketsService);
  private readonly authService = inject(AuthService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly canView = computed(() => this.permissionsService.hasPermission(PERMISSIONS_SOPORTE.VER_TICKETS));
  protected readonly canCreate = computed(() =>
    this.permissionsService.hasPermission(PERMISSIONS_SOPORTE.CREAR_TICKETS)
  );

  protected readonly showModal = signal(false);
  protected readonly isPanelOpen = signal(false);
  protected readonly metrics = signal<SupportMetricsDto | null>(null);
  protected readonly isLoadingMetrics = signal(false);
  protected readonly dragPos = signal<{ x: number; y: number }>({ x: 0, y: 0 });

  /** Items de actividad reciente desde 'since'. */
  private readonly activityItemsSignal = signal<TicketActivityItem[]>([]);
  protected readonly activityItems = this.activityItemsSignal.asReadonly();

  /** Fecha desde la que se busca actividad nueva (persiste en localStorage). */
  private activitySince: Date = this.loadSince();

  protected readonly hasActivity = computed(() => this.activityItemsSignal().length > 0);
  protected readonly activityCount = computed(() => this.activityItemsSignal().length);

  // ---------------------------------------------------------------------------
  // POLLING RESILIENTE: pausa offline + backoff (origen: Rollbar FrontedERP/4)
  // ---------------------------------------------------------------------------
  // Se usa `setTimeout` auto-reprogramado (no `setInterval`) para poder variar
  // el intervalo dinámicamente. Dos mecanismos evitan martillar al backend cuando
  // el cliente pierde conexión:
  //   1) Pausa offline: si `navigator.onLine === false`, el tick NO dispara
  //      peticiones (solo reprograma). El evento `online` reanuda con un tick
  //      inmediato y resetea el intervalo.
  //   2) Backoff: si un ciclo de polling falla (ambos endpoints), el intervalo
  //      se duplica hasta `MAX_POLL_INTERVAL_MS` (5 min). El primer éxito lo
  //      resetea a `POLL_INTERVAL_MS` (60 s). `navigator.onLine` puede mentir
  //      (dice `true` sin salida real), por eso el backoff actúa aunque esté online.
  //   3) Circuit-breaker: tras `CIRCUIT_OPEN_THRESHOLD` fallos consecutivos se abre
  //      el circuito y el intervalo salta a `CIRCUIT_COOLDOWN_MS` (15 min). Un 504
  //      sostenido NO es `offline`, así que sin esto el widget seguía martillando cada
  //      5 min durante horas (amplificación del item Rollbar /4). Un éxito o un evento
  //      `online` cierran el circuito y reanudan el ritmo normal.
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private currentIntervalMs = POLL_INTERVAL_MS;
  private consecutiveFailures = 0;
  private readonly onOnline = () => this.handleOnline();

  ngOnInit(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) this.dragPos.set(JSON.parse(saved));
    } catch {
      // localStorage unavailable or invalid JSON
    }

    // Carga inicial silenciosa para mostrar badge si hay actividad nueva
    this.loadData();

    // Reanudar en cuanto vuelva la conexión (sin esperar el intervalo completo).
    window.addEventListener('online', this.onOnline);

    // Programa el primer poll diferido.
    this.scheduleNextPoll();
  }

  ngOnDestroy(): void {
    if (this.pollTimer !== null) clearTimeout(this.pollTimer);
    window.removeEventListener('online', this.onOnline);
  }

  /** Programa el siguiente poll usando el intervalo actual (afectado por el backoff). */
  private scheduleNextPoll(): void {
    if (this.pollTimer !== null) clearTimeout(this.pollTimer);
    this.pollTimer = setTimeout(() => this.pollTick(), this.currentIntervalMs);
  }

  /**
   * Un ciclo de polling. Si el navegador está offline, no dispara peticiones y
   * simplemente reprograma (el evento `online` lo reactivará). Si está online,
   * carga datos y ajusta el backoff según el resultado.
   */
  private pollTick(): void {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.scheduleNextPoll();
      return;
    }
    this.loadData({ onResult: (ok, retryAfterMs) => this.applyBackoff(ok, retryAfterMs) });
    this.scheduleNextPoll();
  }

  /**
   * Ajusta el intervalo según el resultado del ciclo:
   * - Éxito → resetea a `POLL_INTERVAL_MS` (60 s) y cierra el circuito.
   * - Fallo por debajo del umbral → backoff exponencial (cap `MAX_POLL_INTERVAL_MS`, 5 min).
   * - Fallo a partir del umbral → abre el circuito y pasa a un cooldown largo
   *   (`CIRCUIT_COOLDOWN_MS`, 15 min). Un 504 sostenido NO dispara `offline`
   *   (`navigator.onLine` sigue en `true`), así que sin esto el widget se quedaba
   *   martillando cada 5 min durante horas y amplificaba el item Rollbar /4.
   */
  private applyBackoff(succeeded: boolean, retryAfterMs = 0): void {
    if (succeeded) {
      this.consecutiveFailures = 0;
      this.currentIntervalMs = POLL_INTERVAL_MS;
      return;
    }
    this.consecutiveFailures++;
    let next: number;
    if (this.consecutiveFailures >= CIRCUIT_OPEN_THRESHOLD) {
      // Circuito abierto: solo probamos cada `CIRCUIT_COOLDOWN_MS` hasta que un ciclo
      // tenga éxito (cierra el circuito) o vuelva `online` (lo resetea en `handleOnline`).
      next = CIRCUIT_COOLDOWN_MS;
    } else {
      next = Math.min(POLL_INTERVAL_MS * 2 ** this.consecutiveFailures, MAX_POLL_INTERVAL_MS);
    }
    // 429: el backend indicó explícitamente cuánto esperar (`Retry-After`). Lo respetamos como
    // MÍNIMO — nunca acelerar el poll por debajo de lo pedido (evita re-topar el rate limiter,
    // que ahora particiona por usuario y se comparte entre pestañas).
    if (retryAfterMs > next) {
      next = retryAfterMs;
    }
    this.currentIntervalMs = next;
    // Reprograma con el nuevo intervalo (el `scheduleNextPoll` de `pollTick`
    // ya se ejecutó con el intervalo anterior; lo reemplazamos con el nuevo).
    this.scheduleNextPoll();
  }

  /** Al recuperar la conexión: resetea el backoff y dispara un tick inmediato. */
  private handleOnline(): void {
    this.consecutiveFailures = 0;
    this.currentIntervalMs = POLL_INTERVAL_MS;
    this.pollTick();
  }

  onDragEnd(event: CdkDragEnd): void {
    const pos = event.source.getFreeDragPosition();
    this.dragPos.set(pos);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {
      // localStorage unavailable
    }
  }

  togglePanel(): void {
    const opening = !this.isPanelOpen();
    this.isPanelOpen.set(opening);
    if (opening) {
      // Al abrir: refresca datos y marca todo como visto
      this.loadData({ markAsSeen: true });
    }
  }

  closePanel(): void {
    this.isPanelOpen.set(false);
  }

  openCreate(): void {
    this.isPanelOpen.set(false);
    this.showModal.set(true);
  }

  goToTickets(): void {
    this.isPanelOpen.set(false);
    this.router.navigate(['/soporte/tickets']);
  }

  onTicketCreated(): void {
    this.showModal.set(false);
    this.loadData({ markAsSeen: true });
  }

  /**
   * @param options.markAsSeen actualiza `since` y limpia la actividad (al abrir el panel).
   * @param options.onResult callback con el resultado del ciclo para ajustar el backoff.
   *   El ciclo se considera fallido solo si **ambos** endpoints fallan (síntoma de
   *   conexión caída); si al menos uno responde, se considera éxito.
   */
  private loadData(
    options: { markAsSeen?: boolean; onResult?: (succeeded: boolean, retryAfterMs?: number) => void } = {}
  ): void {
    const tenantId = this.authService.user()?.tenantId;
    if (!tenantId) return;

    if (this.isPanelOpen()) this.isLoadingMetrics.set(true);

    // Cada rama se envuelve para no cortar el forkJoin ante un fallo individual: emite un
    // descriptor { ok, res, err } en vez de propagar el error (el `err` permite leer Retry-After).
    const metrics$ = this.supportService.getMetrics(tenantId).pipe(
      map((res) => ({ ok: true, res, err: null }) as const),
      catchError((err: unknown) => of({ ok: false, res: null, err } as const))
    );

    const activity$ = this.supportService.getActivity(tenantId, this.activitySince).pipe(
      map((res) => ({ ok: true, res, err: null }) as const),
      catchError((err: unknown) => of({ ok: false, res: null, err } as const))
    );

    forkJoin([metrics$, activity$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([metricsResult, activityResult]) => {
        if (metricsResult.ok) {
          this.metrics.set(metricsResult.res?.data ?? null);
        }
        this.isLoadingMetrics.set(false);

        if (activityResult.ok) {
          const data = activityResult.res?.data;
          const items = data?.items ?? [];
          if (options.markAsSeen) {
            // Actualiza 'since' al momento actual → próximo poll no verá estos items
            this.activitySince = new Date();
            this.saveSince(this.activitySince);
            this.activityItemsSignal.set([]);
          } else {
            // Muestra máx 5 items en el panel
            this.activityItemsSignal.set(items.slice(0, 5));
            // Adopta la ventana `since` que aplicó el backend (autoritativa: recortada a 30 días si
            // enviamos una anterior). Evita que nuestro `since` local derive fuera de rango.
            if (data?.since) {
              const serverSince = new Date(data.since);
              if (!Number.isNaN(serverSince.getTime())) {
                this.activitySince = serverSince;
                this.saveSince(serverSince);
              }
            }
          }
        }

        // Fallo del ciclo solo si ambas ramas fallaron (indicativo de conexión).
        const succeeded = metricsResult.ok || activityResult.ok;
        // Si fue un 429, respetamos el Retry-After más alto de las ramas fallidas.
        const retryAfterMs = succeeded
          ? 0
          : Math.max(parseRetryAfterMs(metricsResult.err), parseRetryAfterMs(activityResult.err));
        options.onResult?.(succeeded, retryAfterMs);
      });
  }

  private loadSince(): Date {
    try {
      const raw = localStorage.getItem(STORAGE_SINCE_KEY);
      if (raw) return new Date(raw);
    } catch {
      // localStorage unavailable
    }
    // Por defecto: última hora
    const since = new Date();
    since.setHours(since.getHours() - 1);
    return since;
  }

  private saveSince(date: Date): void {
    try {
      localStorage.setItem(STORAGE_SINCE_KEY, date.toISOString());
    } catch {
      // localStorage unavailable
    }
  }
}
